import React, { useCallback, useEffect, useState } from 'react';

import { DndContext } from '@dnd-kit/core';
import { SortableContext, arrayMove } from '@dnd-kit/sortable';
import { useSnackbar } from 'notistack';
import { useLocation, useParams } from 'react-router-dom';
import { Link } from '~/lib/react-router-dom';

import { Box, Button, Stack } from '@mui/material';
import List from '@mui/material/List';

import { TYPES } from '~/components/Cruises/pages/Inclusions/components/InclusionForm';
import { CruiseInclusion, CruiseInclusionItem } from '~/components/Cruises/pages/Inclusions/types';

import inclusionsService from '~/services/cruises/InclusionsService';

import InclusionsItemCreate from './InclusionsItemCreate';
import InclusionsItemEdit from './InclusionsItemEdit';
import InclusionsItemListItem from './InclusionsItemListItem';

type Props = {
  inclusionType: CruiseInclusion['type'];
};

function InclusionsItemList({ inclusionType }: Props) {
  const [inclusionItems, setInclusionItems] = useState<Array<CruiseInclusionItem>>([]);
  const [fetching, setFetching] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { inclusionId } = useParams<{ inclusionId: string }>();
  const { pathname } = useLocation();
  // `create` and `edit` are literal path segments in App.tsx routes
  // (e.g. `/cruises/inclusions/edit/:inclusionId/items/create`), not route
  // params — derive the action from the URL itself rather than useParams.
  const action: 'create' | 'edit' | undefined = pathname.includes('/items/create')
    ? 'create'
    : pathname.includes('/items/edit')
      ? 'edit'
      : undefined;

  const inclusionsLimit = inclusionType === TYPES.LUX_PLUS ? 1 : 3;

  const handleDragSort = useCallback(
    async ({ active, over }) => {
      if (!!over && active.id !== over.id) {
        const oldIndex = inclusionItems.map((e) => e.id).indexOf(active.id);
        const newIndex = inclusionItems.map((e) => e.id).indexOf(over.id);

        const newList = arrayMove(inclusionItems, oldIndex, newIndex).map((inclusion, index) => ({
          ...inclusion,
          order: index,
        }));

        const maxHighlightedInclusionOrder = Math.max(
          ...newList.filter((inclusion) => inclusion.isHighlighted).map((inclusion) => inclusion.order),
        );

        const hasNormalInclusionBeforeHighlightedInclusions = newList.some(
          (inclusion) => !inclusion.isHighlighted && inclusion.order < maxHighlightedInclusionOrder,
        );

        if (hasNormalInclusionBeforeHighlightedInclusions) {
          enqueueSnackbar('Highlighted inclusions should be at the top of the list', { variant: 'error' });
          return;
        }

        setInclusionItems(newList);

        try {
          await inclusionsService.sortItems(
            inclusionId,
            newList.map((inclusion) => inclusion.id),
          );
          enqueueSnackbar('Inclusions updated', { variant: 'success' });
        } catch (error) {
          enqueueSnackbar('Error updating Inclusions', { variant: 'error' });
        }
      }
    },
    [inclusionItems, setInclusionItems],
  );

  const fetchInclusionItems = useCallback(async () => {
    setFetching(true);

    const response = await inclusionsService.getItems(inclusionId);
    setInclusionItems(response.result);

    setFetching(false);
  }, [inclusionId, setInclusionItems, setFetching]);

  useEffect(() => {
    fetchInclusionItems();
  }, [fetchInclusionItems]);

  return (
    <Box mt={1}>
      <Box>
        <List>
          <DndContext onDragEnd={handleDragSort}>
            <SortableContext items={inclusionItems || []}>
              {inclusionItems.map((inclusionItem) => (
                <InclusionsItemListItem
                  key={inclusionItem.id}
                  inclusionItem={inclusionItem}
                  onChange={fetchInclusionItems}
                  isHighlightable={inclusionType !== TYPES.LUX_PLUS}
                />
              ))}
            </SortableContext>
          </DndContext>
        </List>
      </Box>

      <Stack
        mt={2}
        width="100%"
        direction="row"
        justifyContent="flex-end"
        paddingLeft={2}
        paddingRight={2}
        paddingBottom={1}
      >
        <Button
          component={Link}
          to={`/cruises/inclusions/edit/${inclusionId}/items/create`}
          variant="outlined"
          size="small"
          disabled={inclusionItems.length >= inclusionsLimit}
          fullWidth
        >
          Add Inclusion Item ({inclusionItems.length}/{inclusionsLimit})
        </Button>
      </Stack>

      {action === 'create' && <InclusionsItemCreate onSave={fetchInclusionItems} />}
      {action === 'edit' && <InclusionsItemEdit onSave={fetchInclusionItems} />}
    </Box>
  );
}

export default InclusionsItemList;
